<script lang="ts" setup>
import { Modal, DocumentSearch } from "@/components";
import { ref, PropType } from "vue";

const props = defineProps({
  busy: { type: Boolean, default: false },
  workspace: {
    type: String,
    required: false,
  },
  hasWorkspaceFilter: {
    type: Boolean,
    required: false,
    default: true,
  },
  hasTypeFilter: {
    type: Boolean,
    required: false,
    default: true,
  },
  onlyShareable: {
    type: Boolean,
    required: false,
    default: false,
  },
  singleSelect: {
    type: Boolean,
    required: false,
    default: false,
  },
  excluded: {
    type: Array as PropType<Array<string>>,
    required: false,
    default: [],
  },
  crmContactId: {
    type: String,
    required: false,
    default: null,
  },
  documentSpaceId: {
    type: String,
    required: false,
    default: null,
  },
});

const selection = ref<Map<string, string>>(new Map());
const setSelection = (values: Map<string, string>) => {
  selection.value = values;
};

const emit = defineEmits(["close", "submit"]);
const close = () => {
  if (!props.busy) emit("close");
};

const submit = () => {
  if (props.busy || selection.value.size === 0) return;
  const docIds = Array.from(selection.value.keys());
  const details = docIds.map((id) => ({
    id: id,
    displayTitle: selection.value.get(id),
  }));
  emit("submit", docIds, details);
};
</script>

<template>
  <Modal
    :active="true"
    :scrolling="true"
    :closeDisabled="busy"
    @close="close"
    class="huge"
  >
    <template v-slot:header>
      {{ $t("documentSearch.header") }}
    </template>
    <template v-slot:body>
      <slot name="feedback" />
      <div :inert="busy" :aria-busy="busy">
        <!-- Optional action injected by the caller (e.g. "Add Custom Flow" in the Shared Space editor). Empty in all other contexts. -->
        <slot name="top-action" />
        <DocumentSearch
          :workspace="workspace"
          :hasWorkspaceFilter="hasWorkspaceFilter"
          :onlyShareable="onlyShareable"
          :singleSelect="singleSelect"
          :excluded="excluded"
          :crmContactId="crmContactId"
          :documentSpaceId="documentSpaceId"
          @change="setSelection"
        />
      </div>
    </template>

    <template v-slot:footer>
      <div class="buttons">
        <button
          :class="{
            button: true,
            primary: true,
            disabled: busy || selection.size === 0,
          }"
          :disabled="busy || selection.size === 0"
          @click="submit"
        >
          <span class="material-symbols-outlined">save</span>
          {{ $t("documentSearch.submit", { count: selection.size }) }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.button {
  min-width: 250px;
}
</style>
